import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { partial } from '../../../../util/translation/translation';
import FormCard from '../../../../shared/components/custom/FormCard';
import {
  dailyTimes,
  formatDay,
  occurrenceStarts,
  scheduleSummary,
} from '../seriesUtils';
import { toSchedule } from '../seriesFormValues';

// The preview lists this many dates and summarises the rest
const PREVIEW_LIMIT = 6;

// What the series will look like, from the form as it is filled in: the
// schedule in words and the first occurrences the service will create
const SeriesPreview = ({ values }) => {
  const p = partial('EventSeries');
  if (!values?.scheduleKind) return null;
  const schedule = toSchedule({
    ...values,
    weekdays: values.weekdays || [],
    times: values.times || [],
  });
  const weeksAhead = Number(values.weeksAhead) || 0;
  const starts = occurrenceStarts({
    schedule,
    skipDates: values.skipDates || [],
    weeksAhead,
  });
  const perWeek = (schedule.weekdays.length || 0) * dailyTimes(schedule).length;
  const duration = Number(values.durationMinutes) || 0;
  const maxGuests = Number(values.maxGuests) || 0;
  const firstDayTooFar =
    schedule.startDate &&
    moment(schedule.startDate).isAfter(moment().add(weeksAhead, 'weeks'));

  let footer = p('previewFooter', { weeks: weeksAhead });
  if (starts.length > PREVIEW_LIMIT) {
    footer = `${p('previewMore', { count: starts.length - PREVIEW_LIMIT })} ${footer}`;
  }

  return (
    <FormCard title={p('preview')} className="series-preview">
      <strong className="series-preview__title">
        {values.title || p('untitled')}
      </strong>
      <div className="series-preview__schedule">
        {schedule.weekdays.length ? scheduleSummary(schedule) : p('noDays')}
        {duration > 0 && ` · ${p('minutes', { count: duration })}`}
      </div>
      {!values.eventCategories?.value && (
        <div className="series-preview__muted">
          {maxGuests > 0
            ? p('previewGuests', { count: maxGuests })
            : p('previewNoGuests')}
        </div>
      )}
      <div className="series-preview__stats">
        <span>
          <strong>{starts.length}</strong> {p('occurrences')}
        </span>
        <span>
          <strong>{perWeek}</strong> {p('perWeek')}
        </span>
      </div>
      {starts.length > 0 ? (
        <ul className="series-preview__dates">
          {starts.slice(0, PREVIEW_LIMIT).map((start) => (
            <li key={start.valueOf()}>
              <span>{formatDay(start)}</span>
              <span className="text-muted">
                {start.format('HH:mm')}
                {duration > 0 &&
                  `–${start.clone().add(duration, 'minutes').format('HH:mm')}`}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="series-preview__muted">
          {firstDayTooFar ? p('previewTooFar') : p('previewNone')}
        </p>
      )}
      <small className="series-preview__muted d-block">{footer}</small>
    </FormCard>
  );
};

SeriesPreview.propTypes = {
  values: PropTypes.shape({}),
};

SeriesPreview.defaultProps = {
  values: null,
};

export default SeriesPreview;
